// apps/client/src/hooks/useMarketingSourceReport.ts
import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface SourceCount {
  source: string;
  leads: number;
}

export interface CampaignCount {
  source: string;
  campaign: string;
  leads: number;
}

export interface EnquirySourceCount {
  category: string;
  deals: number;
  isUnknown: boolean;
}

export interface CrmEnquirySourceMonth {
  totalDeals: number;
  knownDeals: number;
  unknownDeals: number;
  /** Null when there are no deals to measure. Zero deals is not 0% coverage. */
  coveragePct: number | null;
  byCategory: EnquirySourceCount[];
}

export interface ProviderSpend {
  provider: string;
  spendCents: number;
  clicks: number;
  impressions: number;
  capturedLeads: number;
  costPerLeadCents: number | null;
}

export interface CampaignSpend {
  campaign: string;
  provider: string;
  spendCents: number;
  clicks: number;
  capturedLeads: number;
  costPerLeadCents: number | null;
}

export interface LeadMonth {
  month: string;
  totalLeads: number;
  withCampaign: number;
  bySource: SourceCount[];
  byCampaign: CampaignCount[];
  spendByProvider: ProviderSpend[];
  spendByCampaign: CampaignSpend[];
  totalSpendCents: number;
  // Optional during rolling deploys and for cached responses from the
  // pre-enquiry-source callable revision.
  crmEnquirySource?: CrmEnquirySourceMonth;
  /**
   * True while this month is still running on the business clock. A part month
   * must not be plotted as a complete one or differenced against a full one.
   * Optional so a response from an older revision still renders.
   */
  monthToDate?: boolean;
  /** False when ad_spend_daily has no row at all for the month. */
  hasSpendRows?: boolean;
  /** False when the month exists only because of spend or CRM deals. */
  hasLeadRows?: boolean;
}

export interface MarketingSourceTotals {
  totalLeads: number;
  withCampaign: number;
  campaignCoveragePct: number | null;
  distinctCampaigns: number;
  distinctSources: number;
  firstCapturedMonth: string | null;
  totalSpendCents: number;
  overallCostPerLeadCents: number | null;
  crmDeals: number;
  crmKnownDeals: number;
  crmUnknownDeals: number;
  crmCoveragePct: number | null;
}

/**
 * Caveats travel in the payload rather than living in the UI, so they cannot
 * drift out of sync with what the query actually returns. `scopeNote` is
 * required rendering: this report covers Valutten-captured leads only, and a
 * reader who takes a month's figure as the group's total lead flow has been
 * misled. It is also the copy that ends up on a shared, unauthenticated page.
 */
export interface MarketingSourceProvenance {
  leadSource: string;
  scopeNote: string;
  enquirySourceAvailable: boolean;
  /** This tenant's own first CRM month, or null when it has none. */
  enquirySourceFirstMonth: string | null;
  enquirySourceNote: string;
  settlementsIncluded: boolean;
  timezone: string;
  spendSource: string;
  spendNote: string;
}

export interface MarketingSourceReport {
  months: LeadMonth[];
  totals: MarketingSourceTotals;
  provenance: MarketingSourceProvenance;
}

interface UseMarketingSourceReportParams {
  companyId: string;
  periodStart?: string;
  periodEnd?: string;
}

interface UseMarketingSourceReportReturn {
  report: MarketingSourceReport | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Monthly lead volumes by source and campaign, for the marketing monthly
 * report. Counts only: the callable returns no commission figures, which is
 * what lets the marketing role (hideCommissions) call it at all.
 */
export function useMarketingSourceReport({
  companyId,
  periodStart,
  periodEnd,
}: UseMarketingSourceReportParams): UseMarketingSourceReportReturn {
  const [report, setReport] = useState<MarketingSourceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchReport = async () => {
      setLoading(true);
      setError(null);

      try {
        const call = httpsCallable<
          { companyId: string; periodStart?: string; periodEnd?: string },
          MarketingSourceReport
        >(functions, 'getMarketingSourceReport');

        // Omit absent bounds rather than passing `undefined`: the callable
        // transport serialises an `undefined` field as JSON `null`, which the
        // server's month-string validator rejects. Spreading conditionally
        // keeps the key off the wire entirely, matching the share modal.
        const result = await call({
          companyId,
          ...(periodStart ? { periodStart } : {}),
          ...(periodEnd ? { periodEnd } : {}),
        });
        if (cancelled) return;
        setReport(result.data);
      } catch (err) {
        if (cancelled) return;
        // Surfaced rather than swallowed: a silent empty state here reads as
        // "no leads this month", which is a very different thing from "the
        // call failed".
        console.error('[useMarketingSourceReport] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load the marketing source report');
        setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [companyId, periodStart, periodEnd, nonce]);

  return { report, loading, error, refetch };
}
